import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Spinner } from "react-bootstrap";

export function SearchableMultiSelect({
  options,
  placeholder = "Search providers...",
  value,
  onChange,
  loading,
  disabled,
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.description.toLowerCase().includes(q));
  }, [query, options]);

  const toggle = useCallback(
    (val) => {
      if (loading || disabled) return;
      const next = selectedSet.has(val)
        ? value.filter((v) => v !== val)
        : [...value, val];
      onChange(next);
    },
    [value, selectedSet, onChange, loading, disabled],
  );

  const removeChip = useCallback(
    (val) => {
      if (loading || disabled) return;
      onChange(value.filter((v) => v !== val));
    },
    [value, onChange, loading, disabled],
  );

  const handleKeyDown = (e) => {
    if (loading || disabled) return;

    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (
      e.key === "Enter" &&
      highlightIndex >= 0 &&
      highlightIndex < filtered.length
    ) {
      e.preventDefault();
      toggle(filtered[highlightIndex].value);
    }
  };

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current?.children[highlightIndex]) {
      listRef.current.children[highlightIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightIndex]);

  useEffect(() => setHighlightIndex(-1), [query]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (loading || disabled) setIsOpen(false);
  }, [loading, disabled]);

  const optionMap = useMemo(
    () => new Map(options.map((o) => [o.value, o.description])),
    [options],
  );

  const selectedOptions = value.map((v) => ({
    value: v,
    description: optionMap.get(v) ?? v,
    isOrphan: !optionMap.has(v),
  }));

  const selectAllFiltered = () => {
    const all = new Set([...value, ...filtered.map((o) => o.value)]);
    onChange([...all]);
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        opacity: loading || disabled ? 0.7 : 1,
        pointerEvents: loading || disabled ? "none" : undefined,
      }}
    >
      <div className="input-group">
        <span className="input-group-text bg-white border-end-0">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            opacity={0.35}
          >
            <circle
              cx="7"
              cy="7"
              r="5"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M10.5 10.5L14 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          className="form-control border-start-0"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (!loading && !disabled) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={loading || disabled}
          style={
            selectedOptions.length > 0
              ? {
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  boxShadow: "none",
                }
              : { boxShadow: "none" }
          }
        />
        <span className="input-group-text bg-white">
          {loading ? (
            <Spinner animation="border" size="sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          ) : (
            <small className="text-muted" style={{ fontSize: 12 }}>
              {value.length} selected
            </small>
          )}
        </span>
      </div>
      {isOpen && !loading && !disabled && (
        <div
          className="border rounded-bottom shadow-sm bg-white"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1060,
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          <div className="d-flex justify-content-between align-items-center px-3 py-1 border-bottom bg-light">
            <small className="text-muted">
              {filtered.length} option{filtered.length !== 1 && "s"}
            </small>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                style={{ fontSize: 12 }}
                onClick={selectAllFiltered}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none text-danger"
                style={{ fontSize: 12 }}
                onClick={() => onChange([])}
              >
                Clear
              </button>
            </div>
          </div>

          <div ref={listRef} role="listbox" aria-multiselectable="true">
            {filtered.length === 0 ? (
              <div
                className="text-center text-muted py-4"
                style={{ fontSize: 14 }}
              >
                No matches for "{query}"
              </div>
            ) : (
              filtered.map((option, i) => {
                const checked = selectedSet.has(option.value);
                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={checked}
                    className={`d-flex align-items-center gap-2 px-3 py-2 ${i === highlightIndex ? "bg-light" : ""}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggle(option.value)}
                    onMouseEnter={() => setHighlightIndex(i)}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      style={{ pointerEvents: "none" }}
                    />
                    <span style={{ fontSize: 14 }}>{option.description}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
